import type {
  CashFlowPoint,
  ChartVisualisation,
  TrendPoint,
  WidgetInstance,
  WidgetPayload
} from "../../../components/widgets/types.ts"
import {
  type SpecIndex,
  adaptResolved
} from "../../DashboardPage/components/Portfolio/widgetsResponseAdapter.ts"
import type {
  CatalogWidgetSpec,
  ResolvedWidget
} from "../../DashboardPage/redux/widgets.types.ts"
import type { ReportWidgetFilters } from "../types/canvas.types.ts"
import { buildWidgetFilterPayload } from "./reportWidgetFilters.ts"

// Sent to POST /dashboard/widgets/resolve/ — resolve_adhoc_widget on the backend
// validates config against an allow-list that already includes "filter" and
// "visualisation"; per-widget config.filter AND-composes with the report-wide
// filter query params server-side.
const buildInstanceConfig = (
  spec: CatalogWidgetSpec,
  filters?: ReportWidgetFilters,
  visualisation?: ChartVisualisation
): Record<string, unknown> => {
  const filterPayload = buildWidgetFilterPayload(filters)
  return {
    ...spec.default_config,
    ...(spec.metric ? { metric: spec.metric } : {}),
    ...(visualisation ? { visualisation } : {}),
    ...(Object.keys(filterPayload).length > 0 ? { filter: filterPayload } : {})
  }
}

interface AdaptedReportWidget {
  widget: WidgetInstance
  payload: WidgetPayload
}

const compareSeriesFrom = (resolved: ResolvedWidget): unknown[] | undefined => {
  const raw = (resolved.payload as { compare_series?: unknown }).compare_series
  return Array.isArray(raw) && raw.length > 0 ? raw : undefined
}

// Selecting a compare window does not mean the tile gains a delta row: the
// backend only emits a compare block for flow metrics, and the KPI card hides
// the badge outright once the window itself has no data.
const rendersCompareRow = (resolved: ResolvedWidget | null): boolean => {
  const payload = resolved?.payload as
    | { compare?: unknown; has_data?: boolean }
    | undefined
  if (!payload?.compare) return false
  return payload.has_data !== false
}

// Overlays the compare-window series onto whichever time-series chart the base
// adapter produced (trend or cash-flow). Breakdown/status charts carry no time
// axis, so they are left untouched.
const withCompareOverlay = (
  payload: WidgetPayload,
  resolved: ResolvedWidget,
  compareLabel: string | undefined
): WidgetPayload => {
  const chart = payload.chart
  if (!chart) return payload
  const compareSeries = compareSeriesFrom(resolved)
  if (!compareSeries) return payload
  if (chart.trend) {
    return {
      ...payload,
      chart: {
        ...chart,
        trend: {
          ...chart.trend,
          compareSeries: compareSeries as TrendPoint[],
          compareLabel
        }
      }
    }
  }
  if (chart.cashFlow) {
    return {
      ...payload,
      chart: {
        ...chart,
        cashFlow: {
          ...chart.cashFlow,
          compareSeries: compareSeries as CashFlowPoint[],
          compareLabel
        }
      }
    }
  }
  return payload
}

// Reuses the dashboard's kpi/chart/feed/alert/rows payload shaping, then overlays the
// report-specific, always-known catalog spec (looked up by key, not by metric), the
// per-widget title override, and the compare-window overlay.
const adaptReportWidget = (
  resolved: ResolvedWidget,
  spec: CatalogWidgetSpec | undefined,
  specIndex: SpecIndex,
  titleOverride: string | undefined,
  currency: string | undefined,
  compareLabel?: string
): AdaptedReportWidget => {
  const base = adaptResolved(resolved, specIndex, currency)
  const effectiveTitle = titleOverride || spec?.title || base.widget.title || ""

  const widget: WidgetInstance = {
    ...base.widget,
    type: (spec?.type ?? base.widget.type) as WidgetInstance["type"],
    dataSource: spec?.data_source ?? base.widget.dataSource,
    title: effectiveTitle,
    capabilities:
      (spec?.capabilities as WidgetInstance["capabilities"]) ??
      base.widget.capabilities,
    drill_to: resolved.drill_to || spec?.drill_to || base.widget.drill_to
  }

  const titled: WidgetPayload = base.payload.tile
    ? {
        ...base.payload,
        tile: {
          ...base.payload.tile,
          title: effectiveTitle,
          ...(compareLabel && base.payload.tile.compare ? { compareLabel } : {})
        }
      }
    : base.payload
  const payload = withCompareOverlay(titled, resolved, compareLabel)

  return { widget, payload }
}

export {
  buildInstanceConfig,
  adaptReportWidget,
  rendersCompareRow,
  type AdaptedReportWidget
}
